import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Field } from '../ui/Field';
import { btnDestructive, btnGhost, btnSecondary, inputCls } from '../ui/styles';
import { StaffAccess } from './StaffAccess';
import { useReauth } from '../useReauth';
import { inviteStaff, revokeStaffLogin, stopStaff } from '../adminApi';
import { ApiError } from '../../core/api/http';
import { isValidEmail } from '../../core/rules/contact';
import { initialsOf } from '../../core/rules/salonDays';
import type { StaffProfile } from '../../core/types/site';
import type { StaffLogin } from '../../core/types/adminRequests';
import type { Treatment } from '../../core/types/catalog';
type Props = {
  profile: StaffProfile;
  treatments: Treatment[];
  login: StaffLogin | null;
  loginsUnavailable: boolean;
  onChange: (patch: Partial<StaffProfile>) => void;
  onLogin: (login: StaffLogin | null) => void;
  /** Sarahs eigen profiel staat bij Salon & contact › Over jou. */
  onOpenProfile: () => void;
};
const dot: Record<StaffProfile['color'], string> = {
  clay: 'bg-clay',
  sage: 'bg-sage',
  rose: 'bg-blush',
  ink: 'bg-ink'
};

/**
 * Eén medewerker: naam, behandelingen, de login, en stoppen. Stoppen is één handeling: login en
 * sessies meteen weg, het profiel (rooster, online boeken) na Opslaan. Sarah zelf staat vast.
 */
export function StaffRow({
  profile: s,
  treatments,
  login,
  loginsUnavailable,
  onChange,
  onLogin,
  onOpenProfile
}: Props) {
  const owner = s.role === 'owner';
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmStop, setConfirmStop] = useState(false);
  const {
    guard,
    dialog
  } = useReauth('Toegang aanpassen vraagt een extra bevestiging.');
  const invite = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) return setError('Vul een geldig e-mailadres in.');
    setBusy(true);
    setError('');
    try {
      onLogin(await inviteStaff(s.id, email.trim()));
      setEmail('');
      toast(`Uitnodiging verstuurd naar ${email.trim()}`, {
        description: `${s.name} kiest zelf een wachtwoord en stelt tweestapsverificatie in.`
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Uitnodigen lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  /** Stoppen in één handeling: login en alle sessies meteen weg (idempotent), het profiel na Opslaan. */
  const stop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (login) {
        const res = await guard(() => stopStaff(s.id));
        if (!res.done) return;
        onLogin(null);
      }
      onChange({
        active: false
      });
      setConfirmStop(false);
      toast(`${s.name} is gestopt`, {
        description: login ? 'Ze is overal uitgelogd en kan niet meer inloggen. Sla op om haar uit het rooster te halen.' : 'Sla op om haar uit het rooster te halen.'
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Stoppen lukte niet: de login is nog actief. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await guard(() => revokeStaffLogin(s.id));
      if (!res.done) return;
      onLogin(null);
      toast(`${s.name} kan niet meer inloggen`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Intrekken lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };
  return <li className="space-y-4 py-4 first:pt-0 last:pb-0" data-id="element-4370" data-mp="2d25a6hz97piu">
      <div className="flex items-center gap-3" data-id="element-4371" data-mp="2d3oq27gzzyk3">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-medium text-shell ${dot[s.color]}`} aria-hidden="true" data-id="element-4372" data-mp="2r02b1qkqqi3b">
          {s.initials}
        </span>
        {owner ? <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-4373" data-mp="wnm4eoo191xx">
            <p className="text-[14px] text-ink" data-id="element-4374" data-mp="rpu2d1tobzg0">
              {s.name} <span className="text-muted" data-id="element-4375" data-mp="u3fpxn1slt34">· eigenaar, doet alle behandelingen</span>
            </p>
            <button type="button" onClick={onOpenProfile} className="text-[13px] text-ink underline underline-offset-2 hover:text-clay" data-id="element-4376" data-mp="y3huq52tr2mm">
              Je profiel aanpassen
            </button>
          </div> : <Field label="Naam" className="min-w-0 flex-1" data-id="element-4377" data-mp="1814aetry6y7n">
            <input value={s.name} onChange={e => onChange({
          name: e.target.value,
          initials: initialsOf(e.target.value)
        })} className={inputCls} autoComplete="off" data-id="element-4378" data-mp="aeyjphse4bqu" />
          </Field>}
      </div>

      {!owner && <>
          <fieldset data-id="element-4379" data-mp="p4385gps3c7e">
            <legend className="text-[13px] text-ink" data-id="element-4380" data-mp="p4cxr36wvhck">Behandelingen</legend>
            <label className="mt-2 flex items-center gap-2.5 text-[14px] text-ink" data-id="element-4381" data-mp="3nu3cotg226jo">
              <input type="checkbox" checked={s.allTreatments} onChange={e => onChange({
            allTreatments: e.target.checked
          })} className="h-4 w-4 accent-clay" data-id="element-4382" data-mp="3ssekyzqpc8zs" />
              Alle behandelingen
            </label>
            {!s.allTreatments && <div className="mt-2 grid gap-x-4 gap-y-1.5 sm:grid-cols-2" data-id="element-4383" data-mp="3galpn63lqyid">
                {treatments.map(t => <label key={t.slug} className="flex items-center gap-2.5 text-[13px] text-ink" data-id="element-4384" data-mp="3bd2bs7atgnqy">
                    <input type="checkbox" checked={s.treatments.includes(t.slug)} onChange={e => onChange({
              treatments: e.target.checked ? [...s.treatments, t.slug] : s.treatments.filter(x => x !== t.slug)
            })} className="h-4 w-4 accent-clay" data-id="element-4385" data-mp="36dz5ykn0idhx" />
                    {t.name}
                  </label>)}
              </div>}
            {!s.allTreatments && s.treatments.length === 0 && <p className="mt-1.5 text-[12px] text-clay" data-id="element-4386" data-mp="2em04xjv0kkif">Zonder behandelingen kan niemand online bij {s.name} boeken.</p>}
          </fieldset>

          <fieldset className="space-y-3" data-id="element-4387" data-mp="28yg3yocbq0o7">
            <legend className="text-[13px] text-ink" data-id="element-4388" data-mp="2909am3mxskw7">Op de website</legend>
            <p className="text-[12px] text-muted" data-id="element-4389" data-mp="1uzotdbwo6x4t">Bij "Bij wie wil je komen?" in het boeken. Zonder foto tonen we haar initialen.</p>
            <label className="flex items-start gap-2.5 text-[14px] text-ink" data-id="element-4390" data-mp="1zy9sf806raro">
              <input type="checkbox" checked={s.onSite === true} disabled={!s.active} onChange={e => onChange({
            onSite: e.target.checked || undefined
          })} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-4391" data-mp="2novoe0wx14lb" />
              <span data-id="element-4392" data-mp="2ivu6rl5bqqy1">
                Toon op de salonpagina
                <span className="block text-[12px] text-muted" data-id="element-4393" data-mp="36mgfzfr2eh9m">
                  {s.active ? 'Met foto, alinea en behandelingen in het blok “Ook in de salon”. Zet het pas aan als haar profiel klaar is.' : 'Gestopt: niet op de site.'}
                </span>
              </span>
            </label>
            <Field label="Foto (link)" data-id="element-4394" data-mp="3gqdasjjlktv6">
              <input type="url" value={s.photo ?? ''} onChange={e => onChange({
            photo: e.target.value || undefined
          })} placeholder="https://…" className={inputCls} data-id="element-4395" data-mp="3gpbzqf2l07dg" />
            </Field>
            <Field label="Eén zin" hint="Bij de keuze in het boeken, bijv. “Gespecialiseerd in rosacea en roodheid”." data-id="element-4396" data-mp="3pif6y5mmvnzn">
              <input value={s.intro ?? ''} maxLength={90} onChange={e => onChange({
            intro: e.target.value || undefined
          })} className={inputCls} data-id="element-4397" data-mp="31tvz1r7hxd83" />
            </Field>
            <Field label="Over haar" hint="Een korte alinea voor de salonpagina en het boeken." data-id="element-4398" data-mp="3brtm4bdmd3z4">
              <textarea value={s.bio ?? ''} rows={3} maxLength={600} onChange={e => onChange({
            bio: e.target.value || undefined
          })} className={inputCls} data-id="element-4399" data-mp="36ro2nihi6ns4" />
            </Field>
          </fieldset>

          <div className="rounded-md bg-shell px-3 py-3" data-id="element-4400" data-mp="m28derxxn74l">
            <p className="text-[13px] text-ink" data-id="element-4401" data-mp="m1q875talikj">Inloggen in het beheer</p>
            {loginsUnavailable ? <p className="mt-1 text-[13px] text-muted" data-id="element-4402" data-mp="2ggdltyi5pqi1">De logins konden niet geladen worden. Ververs de pagina om het opnieuw te proberen.</p> : login ? <>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[13px]" data-id="element-4403" data-mp="2pgs933eolfo6">
                  <span className="text-muted" data-id="element-4404" data-mp="2uboct0ta5rh4">
                    {login.email} · {login.status === 'uitgenodigd' ? 'uitgenodigd, nog niet ingelogd' : 'actief'}
                  </span>
                  <button type="button" onClick={() => void revoke()} disabled={busy} className={btnGhost} data-id="element-4405" data-mp="345y6qmxvexty">
                    Login intrekken
                  </button>
                </div>
                {/* key: na een antwoord van de server begint het formulier opnieuw bij wat er nu staat. */}
                <StaffAccess key={`${(login.roles ?? []).join(',')}|${login.validUntil ?? ''}`} name={s.name} staffId={s.id} login={login} onLogin={onLogin} guard={guard} data-id="element-4406" data-mp="1dx6a0ad9opcz" />
              </> : <form onSubmit={invite} noValidate className="mt-2 flex flex-wrap items-start gap-2" data-id="element-4407" data-mp="11l3ze38i40gl">
                <label className="sr-only" htmlFor={`invite-${s.id}`} data-id="element-4408" data-mp="wm0zsybaimg0">
                  E-mailadres van {s.name}
                </label>
                <input id={`invite-${s.id}`} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="e-mailadres" className={`${inputCls} min-w-0 flex-1`} aria-invalid={Boolean(error)} aria-describedby={error ? `invite-${s.id}-error` : undefined} disabled={!s.active} data-id="element-4409" data-mp="1u8qcc23mchg4" />
                <button type="submit" disabled={busy || !s.active} aria-busy={busy} className={btnSecondary} data-id="element-4410" data-mp="4ubsnw7gjkrh">
                  {busy ? 'Versturen…' : 'Uitnodigen'}
                </button>
                {error && <p id={`invite-${s.id}-error`} role="alert" className="basis-full text-[12px] text-clay" data-id="element-4411" data-mp="1ixkxjwcjn534">
                    {error}
                  </p>}
              </form>}
          </div>

          {s.active ? confirmStop ? <div role="alertdialog" aria-labelledby={`stop-${s.id}`} className="rounded-md bg-shell px-3 py-3" data-id="element-4412" data-mp="26lmcpo489jri">
                <p id={`stop-${s.id}`} className="text-[13px] text-ink" data-id="element-4413" data-mp="21lzpcluoqn4z">
                  {s.name} stoppen?
                </p>
                <p className="mt-1 text-[13px] text-muted" data-id="element-4414" data-mp="2fm12azo85l0t">
                  {login ? 'Haar login wordt meteen ingetrokken. ' : ''}Na Opslaan staat ze niet meer in het rooster en kan niemand online bij haar boeken. Oude afspraken houden
                  haar naam; komende afspraken verzet je met Afwezig melden in de Agenda.
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-4415" data-mp="2rwjmwohj8ki8">
                  <button type="button" onClick={() => void stop()} disabled={busy} aria-busy={busy} className={btnDestructive} data-id="element-4416" data-mp="2rz4rhl97tejo">
                    {busy ? 'Stoppen…' : 'Stoppen'}
                  </button>
                  <button type="button" onClick={() => setConfirmStop(false)} disabled={busy} className={btnGhost} data-id="element-4417" data-mp="2j2x6c5lwbntm">
                    Annuleren
                  </button>
                </div>
              </div> : <button type="button" onClick={() => setConfirmStop(true)} className="text-[13px] text-muted underline underline-offset-2 hover:text-clay" data-id="element-4418" data-mp="2e66fqocgt9ty">
                {s.name} stopt bij de salon…
              </button> : <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted" data-id="element-4419" data-mp="247qxzbzbvcx4">
              Gestopt: niet in het rooster, geen login.
              <button type="button" onClick={() => onChange({
          active: true
        })} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-4420" data-mp="2gm8omkvaofy">
                Weer actief maken
              </button>
            </p>}
        </>}
      {dialog}
    </li>;
}